import { useEffect, useRef, useState } from "react";
import { useCart } from "../../contexts/Cart/CartContext";
import { CartItem } from "../../Interfaces/Types";
import { ImprovedParticipant } from "../../Interfaces/FundraiserRole";
import { generateUniqueId } from "../../Interfaces/Types";
import { usePopup } from "../../components/Popup/PopupContext";
import PopupModel from "../../components/Popup/PopupModel";
import { useNavigate } from "react-router-dom";
import config from "../../config";
import { formatPossessive } from "../../CommonFunctions";
import Button from "@mui/material/Button";

interface AddToCartButtonProps {
  item: CartItem;
  participant: ImprovedParticipant;
  buttonText?: string;
  className?: string;
  disabled?: boolean;
  buttonVariant?: "contained" | "text" | "outlined";
  isFromSamplePage?: boolean;
}

const useAfterStateChange = (stateKey: any, callback: () => void) => {
  const previousState = useRef(stateKey);

  useEffect(() => {
    if (previousState.current !== stateKey && stateKey !== undefined) {
      callback();
    }
    previousState.current = stateKey;
  }, [stateKey, callback]);
};

const AddToCartButton = ({
  item,
  participant,
  buttonText = "Add to Cart",
  className = "",
  disabled = false,
  buttonVariant = "contained",
  isFromSamplePage = false,
}: AddToCartButtonProps) => {
  const { state, dispatch, toggleDrawer, isDrawerOpen, setDrawerOpen } =
    useCart();
  const navigate = useNavigate();
  const { showPopup, hidePopup } = usePopup();
  const [curItem, setCurItem] = useState<CartItem | null>();
  const [errorVal, setErrorVal] = useState<string[]>([]);

  const handleDiffParticipantsPopup = (
    bodyText: string,
    curParticipantName: string,
    part_id: string,
    newItem: CartItem,
  ) => {
    const checkInfoAndContinuePopup = new PopupModel(
      "Different Participant Found in Cart!",
      <div>{bodyText}</div>,
      [
        {
          label: "Checkout",
          onClick: () => {
            navigate("/checkout/cart");
            hidePopup();
          },
        },
        {
          label: "Empty cart and add current item",
          onClick: () => {
            setDrawerOpen(true);
            dispatch({ type: "CLEAR_CART" });
            dispatch({ type: "REMOVE_POPUP_ERROR" });
            if (curItem) {
              dispatch({
                type: "ADD_ITEM",
                payload: { item: newItem, participant },
              });
            }
            hidePopup();
          },
        },
        //TODO this doesnt seem to actually navigate back to the participant shop page, need to investigate
        // the url changes but the page doesnt, need some refresh?
        {
          label: `Return to ${formatPossessive(curParticipantName)} page`,
          onClick: () => {
            navigate(`/participant-shop/part-id/${part_id}`);
            hidePopup();
          },
        },
      ],
      "40vw",
    );

    setDrawerOpen(false);
    showPopup(
      checkInfoAndContinuePopup.header,
      checkInfoAndContinuePopup.body,
      checkInfoAndContinuePopup.buttons,
      checkInfoAndContinuePopup.width,
    );
  };

  const sampleShopPopupAlert = () => {
    const sampleShopPopup = new PopupModel(
      "Sample Shop", // header
      <div>
        <p>If this were a real shop, your item would be added to the cart.</p>
        <p>Items cannot be added to the cart in the sample shop.</p>
      </div>, //body
      [
        {
          label: "Okay, got it",
          onClick: () => {
            hidePopup();
          },
        },
      ],
      "40vw", //width
    );
    showPopup(
      sampleShopPopup.header,
      sampleShopPopup.body,
      sampleShopPopup.buttons,
      sampleShopPopup.width,
    );
  };

  useEffect(() => {
    if (errorVal.length > 0 && curItem) {
      handleDiffParticipantsPopup(
        errorVal[0],
        errorVal[1],
        errorVal[2],
        curItem,
      );
      setErrorVal([]);
      setCurItem(null);
    }
  }, [errorVal]);

  useAfterStateChange(state.error, () => {
    if (state.error) {
      // if the error was set, we have two participants in use, send out the popup
      setErrorVal(state.error);
    }
  });

  const handleAddToCart = () => {
    if (disabled) return;
    if (isFromSamplePage) {
      sampleShopPopupAlert();
      return;
    }

    if (item.sku === config.DONATION_ITEM_ID) {
      // Send the unedited donation item directly
      const newItem = {
        ...item, // Use the original item properties
        id: generateUniqueId(),
      };
      setCurItem(newItem);
      dispatch({
        type: "ADD_ITEM",
        payload: { item: newItem, participant },
      });
      setDrawerOpen(true);
      return;
    }

    // Find if the item already exists in the cart with the same SKU and customization
    const existingItem = state.cart.find(
      (cartItem) =>
        cartItem.sku === item.sku &&
        JSON.stringify(cartItem.customization) ===
          JSON.stringify(item.customization),
    );

    // Send the item to the cart to be added (the cart will increment the quantity
    //     if it exists or add a new item)
    const newItem = existingItem || {
      ...item, // Retain other properties of the item
      id: generateUniqueId(),
    };
    setCurItem(newItem);
    dispatch({
      type: "ADD_ITEM",
      payload: { item: newItem, participant },
    });
    setDrawerOpen(true);
  };
  return (
    <Button
      onClick={handleAddToCart}
      className={className}
      disabled={disabled}
      variant={buttonVariant}
    >
      {buttonText}
    </Button>
  );
};

export default AddToCartButton;
